import { apiRequest } from './api';
import { ApiResponse, IComissao } from '../types';

interface ComissaoFilters {
  page?: number;
  limit?: number;
  mes_referencia?: string;
  funcionario_id?: number;
  tipo?: string;
  status?: string;
  escritorio_id?: number;
  sortBy?: string;
  sortOrder?: string;
}

interface ApuracaoResult {
  vendas: { criadas: number; jaExistentes: number; erros: string[] };
  recebimentos: { criadas: number; jaExistentes: number; erros: string[] };
  totalCriadas: number;
  totalJaExistentes: number;
  erros: string[];
}

interface ResumoVendedor {
  funcionario: { id: number; nome: string; cargo: string; foto?: string };
  total_pendente: number;
  total_aprovada: number;
  total_paga: number;
  total_cancelada: number;
  total_geral: number;
  qtde: number;
}

interface EstatisticasComissao {
  total: number;
  pendentes: number;
  aprovadas: number;
  pagas: number;
  canceladas: number;
  valor_pendentes: number;
  valor_aprovadas: number;
  valor_pagas: number;
}

interface LancamentoDespesa {
  despesa_id: number;
  funcionario_id: number;
  funcionario_nome: string;
  valor: number;
  qtde_comissoes: number;
}

class ComissaoServiceClass {
  async getComissoes(filters: ComissaoFilters = {}): Promise<ApiResponse<IComissao[]>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
    return await apiRequest.get<IComissao[]>(`/comissoes?${params.toString()}`);
  }

  async apurarComissoes(mesReferencia: string): Promise<ApiResponse<ApuracaoResult>> {
    return await apiRequest.post<ApuracaoResult>('/comissoes/apurar', { mes_referencia: mesReferencia });
  }

  async aprovarComissoes(ids: number[]): Promise<ApiResponse<{ aprovadas: number }>> {
    return await apiRequest.post<{ aprovadas: number }>('/comissoes/aprovar', { ids });
  }

  async cancelarComissao(id: number, observacoes?: string): Promise<ApiResponse<IComissao>> {
    return await apiRequest.patch<IComissao>(`/comissoes/${id}/cancelar`, { observacoes });
  }

  async lancarDespesas(ids: number[], dataVencimento: string): Promise<ApiResponse<LancamentoDespesa[]>> {
    return await apiRequest.post<LancamentoDespesa[]>('/comissoes/lancar-despesas', { ids, data_vencimento: dataVencimento });
  }

  async getResumo(mesReferencia: string, escritorioId?: number): Promise<ApiResponse<ResumoVendedor[]>> {
    const params = new URLSearchParams({ mes_referencia: mesReferencia });
    if (escritorioId) params.append('escritorio_id', escritorioId.toString());
    return await apiRequest.get<ResumoVendedor[]>(`/comissoes/resumo?${params.toString()}`);
  }

  async getEstatisticas(mesReferencia?: string): Promise<ApiResponse<EstatisticasComissao>> {
    const params = new URLSearchParams();
    if (mesReferencia) params.append('mes_referencia', mesReferencia);
    return await apiRequest.get<EstatisticasComissao>(`/comissoes/estatisticas?${params.toString()}`);
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  }

  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      pendente: 'Pendente',
      aprovada: 'Aprovada',
      paga: 'Paga',
      cancelada: 'Cancelada',
    };
    return labels[status] || status;
  }

  getStatusColor(status: string): 'warning' | 'info' | 'success' | 'error' | 'default' {
    const colors: Record<string, 'warning' | 'info' | 'success' | 'error' | 'default'> = {
      pendente: 'warning',
      aprovada: 'info',
      paga: 'success',
      cancelada: 'error',
    };
    return colors[status] || 'default';
  }

  getTipoLabel(tipo: string): string {
    const labels: Record<string, string> = {
      venda: 'Venda',
      recebimento: 'Recebimento',
    };
    return labels[tipo] || tipo;
  }
}

const comissaoService = new ComissaoServiceClass();
export default comissaoService;
